import { useState } from 'react'
import { Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/members/pagination'
import { useInvoices } from '@/hooks/use-billing'
import { creditLeft, creditReason } from '@/lib/credit-notes'
import { formatKES } from '@/lib/utils'
import { CreditStatusBadge } from './credit-status-badge'
import { InvoiceDetailModal } from './invoice-detail-modal'
import type { Invoice } from '@riverpark/types'

const PAGE_SIZE = 20

function formatDate(unixSeconds?: number) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function CreditNotesListTab() {
  const [page, setPage] = useState(1)
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null)

  const { data, isLoading } = useInvoices({
    invoiceType: 'credit_note',
    page,
    limit: PAGE_SIZE,
  })
  const creditNotes = data?.data ?? []
  const meta = data?.meta ?? { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 }

  return (
    <div className="space-y-4">
      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading credit notes…</p>
      ) : creditNotes.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No credit notes yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-border-2">
          <table className="w-full font-sans text-sm">
            <thead>
              <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                <th className="px-4 py-3">Credit note #</th>
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Plot</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">What it's for</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Used</th>
                <th className="px-4 py-3">Left</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {creditNotes.map((c) => (
                <tr key={c.id} className="border-b border-border-2 last:border-0">
                  <td className="px-4 py-3 font-medium text-ink">{c.invoiceNumber}</td>
                  <td className="px-4 py-3 text-ink">{c.memberName}</td>
                  <td className="px-4 py-3 text-ink">{c.plotNumber}</td>
                  <td className="px-4 py-3 text-ink">{formatDate(c.createdAt)}</td>
                  <td className="px-4 py-3 text-ink">{creditReason(c)}</td>
                  <td className="px-4 py-3 text-ink">{formatKES(Math.abs(c.amount))}</td>
                  <td className="px-4 py-3 text-ink">
                    {c.amountPaid > 0 ? formatKES(c.amountPaid) : '—'}
                  </td>
                  <td className="px-4 py-3 text-ink">
                    {creditLeft(c) > 0 ? formatKES(creditLeft(c)) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <CreditStatusBadge credit={c} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="secondary" size="sm" onClick={() => setActiveInvoice(c)}>
                      <Eye size={14} className="mr-1.5" />
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {meta.total > 0 && (
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={meta.limit}
          onPageChange={setPage}
          emptyLabel="No credit notes"
          className="rounded border border-border-2 bg-card"
        />
      )}

      {activeInvoice && (
        <InvoiceDetailModal invoice={activeInvoice} onClose={() => setActiveInvoice(null)} />
      )}
    </div>
  )
}
